import { useState, useCallback } from "react";
import moment from "moment-timezone";

/**
 * Custom hook for table sorting functionality
 * @param {Array} data - The data array to sort
 * @param {string} defaultSortField - Default field to sort by
 * @param {string} defaultSortDirection - Default sort direction ('asc' or 'desc')
 * @returns {Object} - Sorting state and functions
 */
export const useTableSorting = (
  data = [],
  defaultSortField = null,
  defaultSortDirection = "asc"
) => {
  const [sortConfig, setSortConfig] = useState({
    field: defaultSortField,
    direction: defaultSortDirection,
  });

  /**
   * Handle sorting when arrow is clicked
   * @param {string} field - The field to sort by
   * @param {Event} event - Click event
   */

  const handleSort = useCallback((field, event) => {
    if (event.detail === 2 || event.type === "dblclick") {
      setSortConfig({ field: null, direction: "asc" });
      return;
    }

    if (event.direction) {
      setSortConfig({ field, direction: event.direction });
      return;
    }

    setSortConfig((prevConfig) => {
      if (prevConfig.field === field) {
        return {
          field,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          field,
          direction: "asc",
        };
      }
    });
  }, []);

  /**
   * Get sort direction for a specific field
   * @param {string} field - The field to check
   * @returns {string|null} - 'asc', 'desc', or null
   */
  const getSortDirection = useCallback(
    (field) => {
      return sortConfig.field === field ? sortConfig.direction : null;
    },
    [sortConfig]
  );

  /**
   * Check if a field is currently being sorted
   * @param {string} field - The field to check
   * @returns {boolean} - True if field is being sorted
   */
  const isFieldSorted = useCallback(
    (field) => {
      return sortConfig.field === field;
    },
    [sortConfig]
  );

  const sortedData = useCallback(() => {
    if (!sortConfig.field || !data.length) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      if (
        sortConfig.field.includes("date") ||
        sortConfig.field.includes("checkin")
      ) {
        // Parse dates using moment with Asia/Kolkata timezone
        aValue = aValue ? moment.tz(aValue, "Asia/Kolkata").valueOf() : 0;
        bValue = bValue ? moment.tz(bValue, "Asia/Kolkata").valueOf() : 0;
      } else {
        // Check if values are numeric strings and convert them to numbers
        const aIsNumeric = !isNaN(aValue) && !isNaN(parseFloat(aValue));
        const bIsNumeric = !isNaN(bValue) && !isNaN(parseFloat(bValue));

        if (aIsNumeric && bIsNumeric) {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  return {
    sortConfig,
    handleSort,
    getSortDirection,
    isFieldSorted,
    sortedData: sortedData(),
  };
};

export default useTableSorting;
